import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Bot, User, Square, SquarePen, Info, Menu, Settings, Paperclip } from 'lucide-react';
import { FavoriteButton } from '../../components/FavoriteButton';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConversationSidebar } from '@/components/ConversationSidebar';
import { ShareDialog } from '@/components/ShareDialog';
import { EntityParameterPanel } from '@/components/EntityParameterPanel';
import { toast } from '@/components/ui/use-toast';
import { useConversation } from '@/lib/hooks/useConversation';
import { useArcheSession } from '@/lib/hooks/useArcheSession';
import { useModelDefinition, createDefaultParameterValues } from '@/lib/hooks/useModelDefinition';

// GPT-5 reasoning effort levels
type ReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh';
type Verbosity = 'low' | 'medium' | 'high';

// Gemini thinking levels
type ThinkingLevel = 'LOW' | 'HIGH';

// Default model parameters for GPT-4.x
const DEFAULT_GPT4_PARAMS = {
  maxOutputTokens: 1000,
};

// Default model parameters for GPT-5 (only reasoning-compatible params)
const DEFAULT_GPT5_PARAMS = {
  reasoningEffort: 'medium' as ReasoningEffort,
  verbosity: 'medium' as Verbosity,
  maxOutputTokens: 8000,
  reasoning: true, // Default on, but will be set based on model
};

// Helper to determine default reasoning state based on GPT-5 model
const getDefaultGpt5Reasoning = (modelName: string): boolean => {
  const lowerName = modelName.toLowerCase();
  // GPT5-mini and GPT5-nano default to reasoning OFF
  if (lowerName.includes('mini') || lowerName.includes('nano')) {
    return false;
  }
  // GPT5.2 and other GPT-5 models default to reasoning ON
  return true;
};

// Default model parameters for Claude
// Note: maxTokens must be > budgetTokens for thinking to work
const DEFAULT_CLAUDE_PARAMS = {
  maxTokens: 16000, // Must be greater than budgetTokens
  budgetTokens: 10000,
  temperature: 1, // Note: temperature is fixed at 1 when thinking is enabled
};

// Default model parameters for Gemini
const DEFAULT_GEMINI_PARAMS = {
  maxOutputTokens: 8000,
  temperature: 0.7,
  thinkingLevel: 'HIGH' as ThinkingLevel,
};
import { ArcheLayout } from '../../components/ArcheLayout';
import { useSidebar } from '../../context/SidebarContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { authApi } from '@/lib/auth-api';
import { api, ModelProvider } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import { useModels } from '@/lib/hooks/useModels';
import { useTextHighlightLearnMore } from '@/lib/hooks/useTextHighlightLearnMore';
import { useFileContext } from '@/lib/hooks/useFileContext';
import { useFileUpload } from '@/lib/hooks/useFileUpload';
import { LearnMorePopup } from '@/components/LearnMorePopup';
import { FileContextDropZone } from '@/components/FileContextDropZone';
import { AttachedFiles } from '@/components/AttachedFiles';
import { FilePickerDialog } from '@/components/FilePickerDialog';
import { traceStart, traceEnd, traceError, addBreadcrumb, logDebug } from '@/lib/telemetry';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// WebSocket endpoint from environment (now available in both local and staging)
const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';

// Maximum number of messages to send in conversation history
// Keeps payload size manageable and reduces token costs
const MAX_CONVERSATION_MESSAGES = 20;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Full content including file context (for conversation history) */
  contextContent?: string;
  thinking?: string;
  provider?: ModelProvider;
  model?: string;
  isStreaming?: boolean;
}

// Arche ID for single-ai
const ARCHE_ID = 'arche_single_ai';

// Inner component that uses the SearchResults context
function SingleAIContent() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ModelProvider>('openai');
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [includeContext, setIncludeContext] = useState(true);
  const [showThinking, setShowThinking] = useState(false);
  // GPT-4.x specific parameters
  const [gpt4MaxOutputTokens, setGpt4MaxOutputTokens] = useState(
    DEFAULT_GPT4_PARAMS.maxOutputTokens,
  );
  // GPT-5 specific parameters (only reasoning-compatible params)
  const [gpt5Reasoning, setGpt5Reasoning] = useState(DEFAULT_GPT5_PARAMS.reasoning);
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(
    DEFAULT_GPT5_PARAMS.reasoningEffort,
  );
  const [verbosity, setVerbosity] = useState<Verbosity>(DEFAULT_GPT5_PARAMS.verbosity);
  const [maxOutputTokens, setMaxOutputTokens] = useState(DEFAULT_GPT5_PARAMS.maxOutputTokens);
  // Claude specific parameters
  const [claudeMaxTokens, setClaudeMaxTokens] = useState(DEFAULT_CLAUDE_PARAMS.maxTokens);
  const [claudeBudgetTokens, setClaudeBudgetTokens] = useState(DEFAULT_CLAUDE_PARAMS.budgetTokens);
  const [claudeTemperature, setClaudeTemperature] = useState(DEFAULT_CLAUDE_PARAMS.temperature);
  // Gemini specific parameters
  const [geminiMaxOutputTokens, setGeminiMaxOutputTokens] = useState(
    DEFAULT_GEMINI_PARAMS.maxOutputTokens,
  );
  const [geminiTemperature, setGeminiTemperature] = useState(DEFAULT_GEMINI_PARAMS.temperature);
  const [geminiThinkingLevel, setGeminiThinkingLevel] = useState<ThinkingLevel>(
    DEFAULT_GEMINI_PARAMS.thinkingLevel,
  );
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // File picker dialog state
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareExpiresAt, setShareExpiresAt] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // File context for drag-and-drop attachments
  const {
    files: attachedFiles,
    totalSize: attachedFilesSize,
    isProcessing: isProcessingFiles,
    addFiles: addAttachedFiles,
    addFileWithContent,
    removeFile: removeAttachedFile,
    clearFiles: clearAttachedFiles,
    formatContext: formatFileContext,
  } = useFileContext({
    onError: (error) => {
      toast({
        title: 'File attachment error',
        description: `${error.fileName}: ${error.error}`,
        variant: 'destructive',
      });
    },
  });

  // Context folder ID for uploading dropped files
  const [contextFolderId, setContextFolderId] = useState<string | null>(null);
  const contextFolderInitializedRef = useRef(false);

  // File upload for saving dropped files to My-Fias/Context
  const { uploadFiles } = useFileUpload({
    onUploadComplete: (file) => {
      logDebug('context-upload', 'File uploaded to context folder', { fileName: file.fileName });
    },
    onUploadError: (fileName, error) => {
      // Don't show error toast - the file is still attached as context locally
      logDebug('context-upload', 'Failed to save file to context folder', {
        fileName,
        error: error.message,
      });
    },
  });

  // Ensure context folder exists (called once when files are first dropped)
  const ensureContextFolderExists = useCallback(async (): Promise<string | null> => {
    if (contextFolderId) return contextFolderId;
    if (contextFolderInitializedRef.current) return contextFolderId;
    contextFolderInitializedRef.current = true;

    try {
      const { folder } = await api.ensureContextFolder();
      setContextFolderId(folder.folderId);
      return folder.folderId;
    } catch (err) {
      logDebug('context-upload', 'Failed to ensure context folder', {
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }, [contextFolderId]);

  // Handle files dropped: read locally for immediate use AND upload to context folder
  const handleFilesDropped = useCallback(
    async (files: FileList | File[]) => {
      // 1. Read file content locally and add as context (immediate)
      await addAttachedFiles(files);

      // 2. Upload to My-Fias/Context folder in the background
      const fileArray = Array.from(files);
      const folderId = await ensureContextFolderExists();
      if (folderId && fileArray.length > 0) {
        // Upload in background - don't await
        uploadFiles(fileArray, { folderId }).catch((err) => {
          logDebug('context-upload', 'Background upload failed', {
            error: err instanceof Error ? err.message : String(err),
          });
        });
      }
    },
    [addAttachedFiles, ensureContextFolderExists, uploadFiles],
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { isCollapsed, isMobile, toggle } = useSidebar();

  // Track if session has been initialized
  const [sessionInitialized, setSessionInitialized] = useState(false);

  // Conversation persistence
  const {
    conversation,
    conversations,
    loading: conversationsLoading,
    createConversation,
    loadConversation,
    addMessage,
    updateTitle,
    archiveConversation,
    deleteConversationById,
    clearConversation,
    refreshList,
  } = useConversation({
    archeId: ARCHE_ID,
  });

  // Session persistence (preferences)
  const {
    session,
    loading: sessionLoading,
    updateSingleAIPreferences,
  } = useArcheSession({
    archeId: ARCHE_ID,
  });

  // Fetch available models from the API
  const {
    models,
    providers,
    modelsByProvider,
    getRecommendedModel,
    getModelById,
    loading: modelsLoading,
    error: modelsError,
    refresh: refreshModels,
  } = useModels();

  // Set default model once models are loaded
  useEffect(() => {
    if (!selectedModelId && models.length > 0) {
      const recommended = getRecommendedModel(selectedProvider);
      if (recommended) {
        setSelectedModelId(recommended.entityId);
      } else if (modelsByProvider[selectedProvider]?.length > 0) {
        setSelectedModelId(modelsByProvider[selectedProvider][0].entityId);
      }
    }
  }, [models, selectedProvider, selectedModelId, getRecommendedModel, modelsByProvider]);

  // Get the currently selected model details
  const selectedModel = useMemo(() => {
    if (!selectedModelId) return null;
    return getModelById(selectedModelId);
  }, [selectedModelId, getModelById]);

  // Fetch model definition for the selected model (provides parameter schema)
  // This enables data-driven UI rendering from the Entity's parameter schema
  const { parameterSchema, loading: modelDefLoading } = useModelDefinition({
    provider: selectedModel?.provider,
    modelName: selectedModel?.modelName,
  });

  // Unified parameter values state - driven by model definition schema
  // Stored per-model so switching models preserves each model's settings
  // Key is modelEntityId, value is the parameter values for that model
  const [allModelParameters, setAllModelParameters] = useState<
    Record<string, Record<string, unknown>>
  >({});

  // Get parameters for the currently selected model
  const modelParameters = useMemo(() => {
    const modelId = selectedModel?.entityId;
    if (!modelId) return {};
    return allModelParameters[modelId] || {};
  }, [allModelParameters, selectedModel?.entityId]);

  // Check if selected model is GPT-4.x (non-reasoning OpenAI model)
  const isGpt4Model = useMemo(() => {
    const modelName = selectedModel?.modelName || '';
    return modelName.startsWith('gpt-4') || modelName.startsWith('gpt-4o');
  }, [selectedModel]);

  // Check if selected model is GPT-5.x (supports reasoning parameters)
  const isGpt5Model = useMemo(() => {
    return selectedModel?.modelName?.startsWith('gpt-5') ?? false;
  }, [selectedModel]);

  // Check if selected model is a Claude model
  const isClaudeModel = useMemo(() => {
    const modelName = selectedModel?.modelName || '';
    return modelName.includes('claude');
  }, [selectedModel]);

  // Check if selected model is a Grok model
  const isGrokModel = useMemo(() => {
    const modelName = selectedModel?.modelName || '';
    return modelName.includes('grok');
  }, [selectedModel]);

  // Check if selected model is a Gemini model
  const isGeminiModel = useMemo(() => {
    const modelName = selectedModel?.modelName || '';
    return modelName.includes('gemini');
  }, [selectedModel]);

  // Check if selected model is Gemini 2.5+ (supports thinking)
  const isGemini25Plus = useMemo(() => {
    const modelName = selectedModel?.modelName || '';
    // Match gemini-2.5, gemini-3, etc. but not gemini-2.0
    return modelName.match(/gemini-(2\.5|[3-9]|[1-9]\d)/) !== null;
  }, [selectedModel]);

  // Track previous model to detect actual model changes vs initial mount
  const prevModelNameRef = useRef<string | null>(null);

  // Set default reasoning state when user changes to a different GPT-5 model
  // GPT5.2 defaults to ON, GPT5-mini/nano defaults to OFF
  // Skip on initial mount to let session restore handle saved preferences
  useEffect(() => {
    const currentModelName = selectedModel?.modelName || null;

    // Only set default when model actually changes (not on initial mount)
    // prevModelNameRef.current is null on first render
    if (
      prevModelNameRef.current !== null &&
      isGpt5Model &&
      currentModelName &&
      currentModelName !== prevModelNameRef.current
    ) {
      const defaultReasoning = getDefaultGpt5Reasoning(currentModelName);
      setGpt5Reasoning(defaultReasoning);
    }

    prevModelNameRef.current = currentModelName;
  }, [isGpt5Model, selectedModel?.modelName]);

  // Smart auto-scroll: only scrolls to bottom if user is already near bottom
  const { containerRef, anchorRef, scrollToBottomIfNeeded, scrollToBottom } = useAutoScroll();

  // Track if we've initialized parameters for this model to avoid resetting user values
  const parametersInitializedRef = useRef<string | null>(null);

  // Initialize model parameters from schema when model definition changes
  // NOTE: This effect only initializes modelParameters state for the EntityParameterPanel.
  // Legacy state variables (gpt5Reasoning, showThinking, etc.) are handled by:
  //   1. Session restore effect (restores saved values or sets defaults)
  //   2. Model change effect (sets defaults when user switches models)
  // We do NOT sync schema defaults to legacy state here to avoid race conditions
  // where schema load overwrites session-restored values.
  useEffect(() => {
    const modelId = selectedModel?.entityId;
    if (parameterSchema.length > 0 && modelId) {
      const defaults = createDefaultParameterValues(parameterSchema);
      setAllModelParameters((prev) => ({
        ...prev,
        [modelId]: {
          ...defaults,
          ...(prev[modelId] || {}), // Preserve any existing values for THIS model
        },
      }));
      parametersInitializedRef.current = selectedModel.modelName || null;
    }
  }, [parameterSchema, selectedModel?.entityId, selectedModel?.modelName]);

  // Handler for parameter changes from EntityParameterPanel
  const handleParameterChange = useCallback(
    (newValues: Record<string, unknown>) => {
      const modelId = selectedModel?.entityId;
      if (modelId) {
        setAllModelParameters((prev) => ({
          ...prev,
          [modelId]: newValues,
        }));
      }
      logDebug('SingleAI', 'Parameter change', { newValues, provider: selectedProvider });

      // Sync with legacy state variables for backwards compatibility
      // This bridges the new parameter system with the existing session persistence
      if (selectedProvider === 'openai') {
        if ('reasoningEnabled' in newValues) {
          setGpt5Reasoning(newValues.reasoningEnabled as boolean);
          updateSingleAIPreferences({
            providerSettings: { openai: { gpt5Reasoning: newValues.reasoningEnabled as boolean } },
          });
        }
        if ('reasoningEffort' in newValues) {
          setReasoningEffort(newValues.reasoningEffort as ReasoningEffort);
          updateSingleAIPreferences({
            providerSettings: {
              openai: { reasoningEffort: newValues.reasoningEffort as ReasoningEffort },
            },
          });
        }
        if ('verbosity' in newValues) {
          setVerbosity(newValues.verbosity as Verbosity);
          updateSingleAIPreferences({
            providerSettings: { openai: { verbosity: newValues.verbosity as Verbosity } },
          });
        }
        if ('showThinking' in newValues) {
          const showThinkingValue = newValues.showThinking as boolean;
          logDebug('SingleAI', 'Setting showThinking state', { value: showThinkingValue });
          setShowThinking(showThinkingValue);
          updateSingleAIPreferences({ showThinking: showThinkingValue });

          // When showThinking is enabled and effort is 'low', upgrade to 'medium'
          // because low effort may skip reasoning entirely
          if (showThinkingValue && reasoningEffort === 'low') {
            logDebug('SingleAI', 'Upgrading reasoningEffort from low to medium for showThinking');
            setReasoningEffort('medium');
            if (modelId) {
              setAllModelParameters((prev) => ({
                ...prev,
                [modelId]: { ...(prev[modelId] || {}), reasoningEffort: 'medium' },
              }));
            }
          }
        }
        if ('maxOutputTokens' in newValues) {
          if (isGpt5Model) {
            setMaxOutputTokens(newValues.maxOutputTokens as number);
            updateSingleAIPreferences({
              providerSettings: {
                openai: { gptMaxOutputTokens: newValues.maxOutputTokens as number },
              },
            });
          } else {
            setGpt4MaxOutputTokens(newValues.maxOutputTokens as number);
            updateSingleAIPreferences({
              providerSettings: {
                openai: { gpt4MaxOutputTokens: newValues.maxOutputTokens as number },
              },
            });
          }
        }
      } else if (selectedProvider === 'anthropic') {
        // Get current/new values for constraint checking
        const currentMaxTokens =
          'maxTokens' in newValues ? (newValues.maxTokens as number) : claudeMaxTokens;
        let currentBudgetTokens =
          'budgetTokens' in newValues ? (newValues.budgetTokens as number) : claudeBudgetTokens;

        // Clamp budgetTokens to maxTokens if it exceeds it
        // (budget_tokens must be less than max_tokens for Claude thinking)
        if (currentBudgetTokens >= currentMaxTokens) {
          currentBudgetTokens = currentMaxTokens;
          // Update both state and modelParameters to reflect the clamped value
          newValues = { ...newValues, budgetTokens: currentBudgetTokens };
          if (modelId) {
            setAllModelParameters((prev) => ({
              ...prev,
              [modelId]: { ...(prev[modelId] || {}), budgetTokens: currentBudgetTokens },
            }));
          }
        }

        if ('maxTokens' in newValues) {
          setClaudeMaxTokens(newValues.maxTokens as number);
          updateSingleAIPreferences({
            providerSettings: { anthropic: { maxTokens: newValues.maxTokens as number } },
          });
          // Also save the clamped budgetTokens if it was adjusted
          if (currentBudgetTokens !== claudeBudgetTokens) {
            setClaudeBudgetTokens(currentBudgetTokens);
            updateSingleAIPreferences({
              providerSettings: { anthropic: { budgetTokens: currentBudgetTokens } },
            });
          }
        }
        if ('budgetTokens' in newValues) {
          setClaudeBudgetTokens(currentBudgetTokens);
          updateSingleAIPreferences({
            providerSettings: { anthropic: { budgetTokens: currentBudgetTokens } },
          });
        }
        if ('temperature' in newValues) {
          setClaudeTemperature(newValues.temperature as number);
          updateSingleAIPreferences({
            providerSettings: { anthropic: { temperature: newValues.temperature as number } },
          });
        }
        // Sync thinkingEnabled to showThinking state (used by streaming logic)
        if ('thinkingEnabled' in newValues) {
          const thinkingValue = newValues.thinkingEnabled as boolean;
          setShowThinking(thinkingValue);
          updateSingleAIPreferences({ showThinking: thinkingValue });
          // When thinking is enabled, ensure temperature is set to 1
          if (thinkingValue) {
            setClaudeTemperature(1);
            if (modelId) {
              setAllModelParameters((prev) => ({
                ...prev,
                [modelId]: { ...(prev[modelId] || {}), temperature: 1 },
              }));
            }
            updateSingleAIPreferences({
              providerSettings: { anthropic: { temperature: 1 } },
            });
          }
        }
      } else if (selectedProvider === 'google') {
        if ('maxOutputTokens' in newValues) {
          setGeminiMaxOutputTokens(newValues.maxOutputTokens as number);
          updateSingleAIPreferences({
            providerSettings: { google: { maxOutputTokens: newValues.maxOutputTokens as number } },
          });
        }
        if ('temperature' in newValues) {
          setGeminiTemperature(newValues.temperature as number);
          updateSingleAIPreferences({
            providerSettings: { google: { temperature: newValues.temperature as number } },
          });
        }
        if ('thinkingLevel' in newValues) {
          setGeminiThinkingLevel(newValues.thinkingLevel as ThinkingLevel);
          updateSingleAIPreferences({
            providerSettings: {
              google: { thinkingLevel: newValues.thinkingLevel as ThinkingLevel },
            },
          });
        }
        // Sync thinkingEnabled to showThinking state (used by streaming logic)
        if ('thinkingEnabled' in newValues) {
          const thinkingValue = newValues.thinkingEnabled as boolean;
          setShowThinking(thinkingValue);
          updateSingleAIPreferences({ showThinking: thinkingValue });
        }
      }
    },
    [
      selectedProvider,
      isGpt5Model,
      updateSingleAIPreferences,
      reasoningEffort,
      selectedModel?.entityId,
    ],
  );

  // Initialize state from session preferences when loaded
  useEffect(() => {
    logDebug('SingleAI', 'Session restore check', {
      hasSession: !!session,
      sessionType: session?.type,
      sessionInitialized,
      modelsLength: models.length,
      activeConversationId:
        session?.type === 'single-ai' ? session.preferences.activeConversationId : null,
    });

    if (session && session.type === 'single-ai' && !sessionInitialized && models.length > 0) {
      const prefs = session.preferences;
      logDebug('SingleAI', 'Restoring session', {
        activeConversationId: prefs.activeConversationId,
        selectedProvider: prefs.selectedProvider,
        selectedModelEntityId: prefs.selectedModelEntityId,
        showThinking: prefs.showThinking,
        openaiSettings: prefs.providerSettings?.openai,
      });

      // Restore provider and model
      if (prefs.selectedProvider) {
        setSelectedProvider(prefs.selectedProvider);
      }
      if (prefs.selectedModelEntityId) {
        setSelectedModelId(prefs.selectedModelEntityId);
      }

      // Restore basic preferences
      setIncludeContext(prefs.includeContext ?? true);
      const restoredShowThinking = prefs.showThinking ?? false;
      setShowThinking(restoredShowThinking);
      // Also sync to modelParameters for EntityParameterPanel
      // Note: OpenAI uses 'showThinking', Claude/Gemini use 'thinkingEnabled'
      const restoredModelId = prefs.selectedModelEntityId;
      if (restoredModelId) {
        setAllModelParameters((prev) => ({
          ...prev,
          [restoredModelId]: {
            ...(prev[restoredModelId] || {}),
            showThinking: restoredShowThinking,
            thinkingEnabled: restoredShowThinking,
          },
        }));
      }

      // Restore provider-specific settings
      if (prefs.providerSettings?.openai) {
        const openai = prefs.providerSettings.openai;
        if (openai.gpt4MaxOutputTokens) setGpt4MaxOutputTokens(openai.gpt4MaxOutputTokens);

        // Determine gpt5Reasoning value (from session or default)
        let restoredGpt5Reasoning: boolean | undefined;
        if (openai.gpt5Reasoning !== undefined) {
          restoredGpt5Reasoning = openai.gpt5Reasoning;
          setGpt5Reasoning(openai.gpt5Reasoning);
        } else {
          // No saved preference - set default based on model
          const restoredModel = models.find((m) => m.entityId === prefs.selectedModelEntityId);
          if (restoredModel?.modelName?.startsWith('gpt-5')) {
            restoredGpt5Reasoning = getDefaultGpt5Reasoning(restoredModel.modelName);
            setGpt5Reasoning(restoredGpt5Reasoning);
          }
        }
        if (openai.reasoningEffort) setReasoningEffort(openai.reasoningEffort);
        if (openai.verbosity) setVerbosity(openai.verbosity);
        if (openai.gptMaxOutputTokens) setMaxOutputTokens(openai.gptMaxOutputTokens);

        // Also sync to modelParameters so EntityParameterPanel shows correct values
        // Map session keys to schema keys (gpt5Reasoning -> reasoningEnabled, etc.)
        if (restoredModelId) {
          setAllModelParameters((prev) => ({
            ...prev,
            [restoredModelId]: {
              ...(prev[restoredModelId] || {}),
              ...(restoredGpt5Reasoning !== undefined && {
                reasoningEnabled: restoredGpt5Reasoning,
              }),
              ...(openai.reasoningEffort && { reasoningEffort: openai.reasoningEffort }),
              ...(openai.verbosity && { verbosity: openai.verbosity }),
              ...(openai.gptMaxOutputTokens && { maxOutputTokens: openai.gptMaxOutputTokens }),
            },
          }));
        }
      }
      if (prefs.providerSettings?.anthropic) {
        const anthropic = prefs.providerSettings.anthropic;
        if (anthropic.maxTokens) setClaudeMaxTokens(anthropic.maxTokens);
        if (anthropic.budgetTokens) setClaudeBudgetTokens(anthropic.budgetTokens);
        if (anthropic.temperature !== undefined) setClaudeTemperature(anthropic.temperature);
      }
      if (prefs.providerSettings?.google) {
        const google = prefs.providerSettings.google;
        if (google.maxOutputTokens) setGeminiMaxOutputTokens(google.maxOutputTokens);
        if (google.temperature !== undefined) setGeminiTemperature(google.temperature);
        if (google.thinkingLevel) setGeminiThinkingLevel(google.thinkingLevel);
      }

      // Load active conversation if there was one, then mark initialized
      if (prefs.activeConversationId) {
        logDebug('SingleAI', 'Loading conversation from session', {
          conversationId: prefs.activeConversationId,
        });
        // Await conversation load before marking session as initialized
        loadConversation(prefs.activeConversationId).then(() => {
          setSessionInitialized(true);
        });
      } else {
        setSessionInitialized(true);
      }
    }
  }, [session, sessionInitialized, models.length, loadConversation]);

  // Mark session as initialized when session loading completes but there's no session to restore
  // This handles: no session, wrong session type, session load errors
  useEffect(() => {
    if (!sessionLoading && !sessionInitialized && models.length > 0) {
      // If session didn't load or is wrong type, mark as initialized so we show the empty state
      if (!session || session.type !== 'single-ai') {
        setSessionInitialized(true);
      }
    }
  }, [sessionLoading, session, sessionInitialized, models.length]);

  // Track if we just created a conversation (to avoid overwriting messages)
  const justCreatedConversationRef = useRef(false);

  // Track the last loaded conversation ID to avoid re-restoring when messages are added
  const lastLoadedConversationIdRef = useRef<string | null>(null);

  // Restore messages from loaded conversation
  // Only restore when explicitly loading an existing conversation, not when creating a new one
  useEffect(() => {
    const currentConvId = conversation?.conversationId || null;

    // Skip if we just created this conversation (messages are already in local state)
    // But still update the ref so we don't re-restore on subsequent updates
    if (justCreatedConversationRef.current) {
      justCreatedConversationRef.current = false;
      lastLoadedConversationIdRef.current = currentConvId;
      return;
    }

    // Only restore if this is a different conversation than we last loaded
    if (currentConvId !== lastLoadedConversationIdRef.current) {
      lastLoadedConversationIdRef.current = currentConvId;

      if (conversation?.messages && conversation.messages.length > 0) {
        const loadedMessages: Message[] = conversation.messages.map((msg) => ({
          id: msg.messageId,
          role: msg.role,
          content: msg.content,
          thinking: msg.thinking || undefined,
          provider: msg.provider || undefined,
          model: msg.modelName || undefined,
          isStreaming: false,
        }));
        setMessages(loadedMessages);
      } else if (!conversation) {
        // Conversation was cleared (deleted or new), clear messages
        setMessages([]);
      }
    }
  }, [conversation]);

  // WebSocket streaming state
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  // Track pending user message for saving after streaming completes
  const pendingUserMessageRef = useRef<{ content: string } | null>(null);

  // WebSocket streaming hook
  const {
    streamingText,
    streamingThinking,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken: authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
  });

  // Combine all node streaming text into one string
  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Combine all node streaming thinking into one string
  const combinedStreamingThinking = useMemo(() => {
    return Object.values(streamingThinking).join('');
  }, [streamingThinking]);

  // Update assistant message with streaming text and thinking
  useEffect(() => {
    if (currentAssistantMessageId && (combinedStreamingText || combinedStreamingThinking)) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? {
                ...msg,
                content: combinedStreamingText,
                thinking: combinedStreamingThinking || undefined,
              }
            : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText, combinedStreamingThinking]);

  // Handle streaming completion
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      // Get the final assistant message
      const assistantMessage = messages.find((m) => m.id === currentAssistantMessageId);

      // Update UI state
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();

      // Save messages to conversation (async, don't await)
      const saveMessages = async () => {
        try {
          // Create conversation if needed
          let convId = conversation?.conversationId;
          let isNewConversation = false;
          if (!convId && pendingUserMessageRef.current) {
            // Generate a title from the first user message (truncate to 100 chars)
            const userContent = pendingUserMessageRef.current.content;
            const title =
              userContent.length > 100 ? userContent.slice(0, 100).trimEnd() + '...' : userContent;

            // Mark that we're creating a new conversation to prevent the restore effect from overwriting messages
            justCreatedConversationRef.current = true;

            const newConv = await createConversation(title);
            convId = newConv?.conversationId;
            isNewConversation = true;
            // Update session with new conversation ID
            if (convId) {
              updateSingleAIPreferences({ activeConversationId: convId });
            }
          }

          if (convId && pendingUserMessageRef.current && assistantMessage) {
            // Save both user and assistant messages
            // Use API directly to avoid stale closure issues with addMessage hook
            // (addMessage captures conversation state which may be stale after createConversation)
            if (isNewConversation) {
              await api.addMessages(convId, [
                { role: 'user', content: pendingUserMessageRef.current.content },
                {
                  role: 'assistant',
                  content: assistantMessage.content,
                  thinking: assistantMessage.thinking,
                  provider: assistantMessage.provider,
                  modelName: assistantMessage.model,
                  modelEntityId: selectedModelId || undefined,
                },
              ]);
            } else {
              await addMessage({ role: 'user', content: pendingUserMessageRef.current.content });
              await addMessage({
                role: 'assistant',
                content: assistantMessage.content,
                thinking: assistantMessage.thinking,
                provider: assistantMessage.provider,
                modelName: assistantMessage.model,
                modelEntityId: selectedModelId || undefined,
              });
            }
            pendingUserMessageRef.current = null;
          }
        } catch (err) {
          console.error('Failed to save messages:', err);
        }
      };
      saveMessages();
    }
  }, [
    streamingComplete,
    currentAssistantMessageId,
    resetStreaming,
    messages,
    conversation,
    createConversation,
    addMessage,
    selectedModelId,
    updateSingleAIPreferences,
  ]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      // Check if it's an authorization error
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED' ||
        streamingError.message.toLowerCase().includes('token');

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  // Reset conversation - clears current and optionally creates new
  const handleReset = useCallback(async () => {
    setMessages([]);
    setInputValue('');
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    pendingUserMessageRef.current = null;
    lastLoadedConversationIdRef.current = null;
    resetStreaming();

    // Clear current conversation in local state (don't delete from server)
    clearConversation();

    // Update session to clear active conversation
    updateSingleAIPreferences({ activeConversationId: null });

    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [resetStreaming, isMobile, clearConversation, updateSingleAIPreferences]);

  // Auto-scroll to bottom when messages change (only if user is already at bottom)
  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  // Focus textarea when streaming completes (skip on mobile)
  useEffect(() => {
    if (!isStreaming && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isStreaming, isMobile]);

  // Listen for global history clear event (from Account Settings)
  useEffect(() => {
    const handleHistoryCleared = () => {
      // Clear local messages and conversation state
      setMessages([]);
      setInputValue('');
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      pendingUserMessageRef.current = null;
      lastLoadedConversationIdRef.current = null;
      resetStreaming();
      clearConversation();
      // Refresh the conversation list to reflect deletion
      refreshList();
    };

    window.addEventListener('fias:history-cleared', handleHistoryCleared);
    return () => {
      window.removeEventListener('fias:history-cleared', handleHistoryCleared);
    };
  }, [resetStreaming, clearConversation, refreshList]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // WebSocket streaming
  const streamWithWebSocket = async (
    assistantMessageId: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
    provider: ModelProvider,
    modelName: string,
    gpt4Params?: {
      maxOutputTokens: number;
    },
    gpt5Params?: {
      reasoningEffort: ReasoningEffort;
      verbosity: Verbosity;
      maxOutputTokens: number;
      reasoningEnabled: boolean;
      showThinking: boolean;
    },
    claudeParams?: {
      maxTokens: number;
      temperature: number;
      thinkingEnabled: boolean;
    },
    geminiParams?: {
      maxOutputTokens: number;
      temperature: number;
      thinkingLevel: ThinkingLevel;
      thinkingEnabled: boolean;
    },
  ) => {
    const traceId = traceStart('chat.streamWithWebSocket', {
      provider,
      model: modelName,
      wsEndpoint: WEBSOCKET_ENDPOINT,
    });
    addBreadcrumb('chat', `Starting WebSocket stream: provider=${provider}, model=${modelName}`);

    try {
      // Start the workflow and get requestId
      addBreadcrumb('chat', 'Calling api.startArche');
      const result = await api.startArche('arche_single_ai', {
        messages: conversationHistory,
        provider: provider,
        model: modelName,
        ...(gpt4Params && {
          gpt4MaxOutputTokens: gpt4Params.maxOutputTokens,
        }),
        ...(gpt5Params && {
          reasoningEffort: gpt5Params.reasoningEffort,
          verbosity: gpt5Params.verbosity,
          maxOutputTokens: gpt5Params.maxOutputTokens,
          reasoningEnabled: gpt5Params.reasoningEnabled,
          showThinking: gpt5Params.showThinking,
        }),
        ...(claudeParams && {
          claudeMaxTokens: claudeParams.maxTokens,
          claudeTemperature: claudeParams.temperature,
          claudeThinkingEnabled: claudeParams.thinkingEnabled,
        }),
        ...(geminiParams && {
          geminiMaxOutputTokens: geminiParams.maxOutputTokens,
          geminiTemperature: geminiParams.temperature,
          geminiThinkingLevel: geminiParams.thinkingLevel,
          geminiThinkingEnabled: geminiParams.thinkingEnabled,
        }),
      });

      addBreadcrumb('chat', `Got requestId: ${result.requestId}`);

      // Set up WebSocket streaming
      setCurrentRequestId(result.requestId);
      setCurrentAssistantMessageId(assistantMessageId);
      traceEnd(traceId, { requestId: result.requestId });
      // The useStreaming hook will handle the rest via effects
    } catch (err) {
      console.error('WebSocket streaming failed:', err);
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));

      // Check if it's an authorization error
      const errorStr = String(err).toLowerCase();
      const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : 'Sorry, something went wrong. Please try again.';

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedInput = inputValue.trim();
    addBreadcrumb(
      'chat',
      `Submit attempt: provider=${selectedProvider}, hasInput=${!!trimmedInput}, isStreaming=${isStreaming}`,
    );

    if (!trimmedInput || isStreaming) {
      addBreadcrumb('chat', `Submit blocked: empty=${!trimmedInput}, streaming=${isStreaming}`);
      return;
    }

    // Generate unique ID for this message pair
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;

    // Format file context if any files are attached
    const fileContextPrefix = formatFileContext();
    const hasFileContext = fileContextPrefix.length > 0;

    // Build content for API (includes file context)
    const contentForApi = hasFileContext
      ? `${fileContextPrefix}User query: ${trimmedInput}`
      : trimmedInput;

    // Add user message
    // - content: displayed in UI (clean user input)
    // - contextContent: used for conversation history (includes file context if any)
    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: trimmedInput,
      ...(hasFileContext && { contextContent: contentForApi }),
    };

    // Track user message for saving after streaming completes
    pendingUserMessageRef.current = { content: trimmedInput };

    // Add placeholder for assistant response
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      provider: selectedProvider,
      model: selectedModel?.name,
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputValue('');
    // Clear attached files from UI after submission (context is preserved in message history)
    if (hasFileContext) {
      clearAttachedFiles();
    }
    setIsStreaming(true);

    // Build conversation history for context (only if includeContext is enabled)
    // Filter out messages with empty content (from failed responses)
    // Truncate to last N messages to keep payload size manageable and reduce token costs
    const relevantMessages = messages.filter((msg) => msg.content.trim().length > 0);
    const allMessages = [...relevantMessages, userMessage];
    const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
    // Use contextContent (with file context) if available, otherwise use content
    const conversationHistory = includeContext
      ? truncatedMessages.map((msg) => ({
          role: msg.role,
          content: msg.contextContent || msg.content,
        }))
      : [{ role: userMessage.role, content: contentForApi }];

    const traceId = traceStart('chat.submit', {
      provider: selectedProvider,
      messageCount: conversationHistory.length,
    });

    try {
      // Pass GPT-4.x parameters when applicable
      const gpt4Params = isGpt4Model
        ? {
            maxOutputTokens: gpt4MaxOutputTokens,
          }
        : undefined;

      // Pass GPT-5/Grok parameters when applicable (both use gpt5Params on backend)
      const gpt5Params =
        isGpt5Model || isGrokModel
          ? {
              reasoningEffort,
              verbosity,
              maxOutputTokens,
              reasoningEnabled: gpt5Reasoning,
              showThinking,
            }
          : undefined;
      logDebug('SingleAI', 'Sending GPT-5/Grok params', {
        gpt5Params,
        showThinkingState: showThinking,
      });

      // Pass Claude parameters when applicable
      const claudeParams = isClaudeModel
        ? {
            maxTokens: claudeMaxTokens,
            ...(showThinking && { budgetTokens: claudeBudgetTokens }),
            temperature: showThinking ? 1 : claudeTemperature, // Temperature must be 1 when thinking is enabled
            thinkingEnabled: showThinking,
          }
        : undefined;

      // Pass Gemini parameters when applicable
      // Only enable thinking for Gemini 2.5+ models
      const geminiParams = isGeminiModel
        ? {
            maxOutputTokens: geminiMaxOutputTokens,
            temperature: geminiTemperature,
            thinkingLevel: geminiThinkingLevel,
            thinkingEnabled: isGemini25Plus && showThinking,
          }
        : undefined;

      await streamWithWebSocket(
        assistantMessageId,
        conversationHistory,
        selectedProvider,
        selectedModel?.modelName || '',
        gpt4Params,
        gpt5Params,
        claudeParams,
        geminiParams,
      );
      traceEnd(traceId, { success: true });
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStop = useCallback(() => {
    // Reset WebSocket streaming
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();

    setIsStreaming(false);
    // Mark any streaming message as complete
    setMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
    );
  }, [resetStreaming]);

  // Handler to submit a query directly (used by learn more feature)
  const submitQueryDirectly = useCallback(
    async (query: string) => {
      if (!query.trim() || isStreaming) return;

      // Generate unique ID for this message pair
      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      // Add user message
      const userMessage: Message = {
        id: userMessageId,
        role: 'user',
        content: query.trim(),
      };

      // Track user message for saving after streaming completes
      pendingUserMessageRef.current = { content: query.trim() };

      // Add placeholder for assistant response
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        provider: selectedProvider,
        model: selectedModel?.name,
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setInputValue('');
      setIsStreaming(true);

      // Scroll to bottom so user can see the new message
      setTimeout(() => scrollToBottom(), 50);

      // Build conversation history for context
      const relevantMessages = messages.filter((msg) => msg.content.trim().length > 0);
      const allMessages = [...relevantMessages, userMessage];
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = includeContext
        ? truncatedMessages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          }))
        : [{ role: userMessage.role, content: userMessage.content }];

      // Pass model-specific parameters
      const gpt4Params = isGpt4Model ? { maxOutputTokens: gpt4MaxOutputTokens } : undefined;
      const gpt5Params =
        isGpt5Model || isGrokModel
          ? {
              reasoningEffort,
              verbosity,
              maxOutputTokens,
              reasoningEnabled: gpt5Reasoning,
              showThinking,
            }
          : undefined;
      const claudeParams = isClaudeModel
        ? {
            maxTokens: claudeMaxTokens,
            ...(showThinking && { budgetTokens: claudeBudgetTokens }),
            temperature: showThinking ? 1 : claudeTemperature,
            thinkingEnabled: showThinking,
          }
        : undefined;
      const geminiParams = isGeminiModel
        ? {
            maxOutputTokens: geminiMaxOutputTokens,
            temperature: geminiTemperature,
            thinkingLevel: geminiThinkingLevel,
            thinkingEnabled: isGemini25Plus && showThinking,
          }
        : undefined;

      await streamWithWebSocket(
        assistantMessageId,
        conversationHistory,
        selectedProvider,
        selectedModel?.modelName || '',
        gpt4Params,
        gpt5Params,
        claudeParams,
        geminiParams,
      );
    },
    [
      isStreaming,
      messages,
      selectedProvider,
      selectedModel,
      includeContext,
      isGpt4Model,
      gpt4MaxOutputTokens,
      isGpt5Model,
      gpt5Reasoning,
      reasoningEffort,
      verbosity,
      maxOutputTokens,
      showThinking,
      isClaudeModel,
      claudeMaxTokens,
      claudeBudgetTokens,
      claudeTemperature,
      isGeminiModel,
      geminiMaxOutputTokens,
      geminiTemperature,
      geminiThinkingLevel,
      isGemini25Plus,
      scrollToBottom,
    ],
  );

  // Text highlight "Learn More" feature
  const highlight = useTextHighlightLearnMore(
    messagesContainerRef,
    submitQueryDirectly,
    isStreaming,
  );

  const getProviderLabel = (provider: ModelProvider) => {
    const providerInfo = providers.find((p) => p.key === provider);
    return providerInfo?.displayName || provider;
  };

  // Handle provider change - reset model to recommended for new provider
  const handleProviderChange = (provider: ModelProvider) => {
    setSelectedProvider(provider);
    // Set to recommended model for this provider
    const recommended = getRecommendedModel(provider);
    let newModelId: string | null = null;
    if (recommended) {
      newModelId = recommended.entityId;
      setSelectedModelId(newModelId);
    } else if (modelsByProvider[provider]?.length > 0) {
      newModelId = modelsByProvider[provider][0].entityId;
      setSelectedModelId(newModelId);
    }

    // Save preference (both provider and auto-selected model)
    updateSingleAIPreferences({ selectedProvider: provider, selectedModelEntityId: newModelId });
  };

  // Handle model change with preference saving
  const handleModelChange = (modelId: string) => {
    setSelectedModelId(modelId);
    updateSingleAIPreferences({ selectedModelEntityId: modelId });
  };

  // Handle loading a conversation from the sidebar
  const handleLoadConversation = useCallback(
    async (conversationId: string) => {
      await loadConversation(conversationId);
      updateSingleAIPreferences({ activeConversationId: conversationId });
    },
    [loadConversation, updateSingleAIPreferences],
  );

  // Handle updating a conversation title
  const handleUpdateTitle = useCallback(
    async (conversationId: string, title: string) => {
      // If it's the active conversation, update it
      if (conversation?.conversationId === conversationId) {
        await updateTitle(title);
      } else {
        // Otherwise update directly via API
        await api.updateConversation(conversationId, { title });
      }
    },
    [conversation, updateTitle],
  );

  // Handle archiving a conversation
  const handleArchiveConversation = useCallback(
    async (conversationId: string) => {
      if (conversation?.conversationId === conversationId) {
        await archiveConversation();
        setMessages([]);
        lastLoadedConversationIdRef.current = null;
        updateSingleAIPreferences({ activeConversationId: null });
      } else {
        await api.updateConversation(conversationId, { status: 'archived' });
      }
      // Always refresh the list to ensure UI is in sync
      await refreshList();
    },
    [conversation, archiveConversation, updateSingleAIPreferences, refreshList],
  );

  // Handle deleting a conversation
  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      const isActiveConversation = conversation?.conversationId === conversationId;

      // Use deleteConversationById which updates local state immediately
      await deleteConversationById(conversationId);

      // If we deleted the active conversation, clear all local UI state
      if (isActiveConversation) {
        setMessages([]);
        lastLoadedConversationIdRef.current = null;
        pendingUserMessageRef.current = null; // Clear any pending messages
        justCreatedConversationRef.current = false; // Reset the flag
        updateSingleAIPreferences({ activeConversationId: null });
      }
    },
    [conversation, deleteConversationById, updateSingleAIPreferences],
  );

  // Handle exporting a conversation
  const handleExportConversation = useCallback(
    async (conversationId: string, format: 'json' | 'markdown') => {
      try {
        const content = await api.exportConversation(conversationId, format);
        if (content) {
          // Create a download
          const blob = new Blob([content], {
            type: format === 'json' ? 'application/json' : 'text/markdown',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `conversation-${conversationId}.${format === 'json' ? 'json' : 'md'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Failed to export conversation:', err);
      }
    },
    [],
  );

  // Handle sharing a conversation
  const handleShareConversation = useCallback(async (conversationId: string) => {
    setShareDialogOpen(true);
    setShareLoading(true);
    setShareError(null);
    setShareUrl(null);
    setShareExpiresAt(null);

    try {
      const result = await api.shareConversation(conversationId);
      const fullUrl = `${window.location.origin}/shared/${result.shareId}`;
      setShareUrl(fullUrl);
      setShareExpiresAt(result.expiresAt);
    } catch (err) {
      console.error('Failed to share conversation:', err);
      setShareError('Failed to create share link. Please try again.');
    } finally {
      setShareLoading(false);
    }
  }, []);

  return (
    <FileContextDropZone onFilesDropped={handleFilesDropped} disabled={isStreaming}>
      <div className="flex flex-col h-full">
        {/* Header with provider selector and controls */}
        <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {/* Sidebar toggle (always rendered to maintain layout, hidden when open) */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggle}
                className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
                title="Open sidebar"
              >
                <Menu className="h-6 w-6" />
              </Button>
              <h1 className="text-lg font-semibold">Single-AI</h1>
              <Link
                to="/marketplace/arches/arche_single_ai"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="View arche details"
              >
                <Info className="h-6 w-6" />
              </Link>
              <FavoriteButton archeId="arche_single_ai" />
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              {/* New chat button - always visible */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isStreaming || messages.length === 0}
                className="gap-1.5 [&_svg]:size-5!"
              >
                <SquarePen />
                <span className="hidden sm:inline">New</span>
              </Button>

              {/* Conversation history sidebar */}
              <ConversationSidebar
                conversations={conversations}
                activeConversationId={conversation?.conversationId || null}
                loading={conversationsLoading}
                onNewConversation={handleReset}
                onLoadConversation={handleLoadConversation}
                onUpdateTitle={handleUpdateTitle}
                onArchiveConversation={handleArchiveConversation}
                onDeleteConversation={handleDeleteConversation}
                onExportConversation={handleExportConversation}
                onShareConversation={handleShareConversation}
                isStreaming={isStreaming}
              />

              {/* Settings gear button - opens settings sheet */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" title="Settings">
                    <Settings className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Settings</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-6 mt-6">
                    {/* Error state with retry */}
                    {isMobile && modelsError && (
                      <div className="space-y-2">
                        <Label className="text-sm text-destructive">Failed to load models</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => refreshModels()}
                          className="w-full"
                        >
                          Retry
                        </Button>
                      </div>
                    )}

                    {/* Provider selector - mobile only */}
                    {isMobile && !modelsError && (
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Provider</Label>
                        <Select
                          value={selectedProvider}
                          onValueChange={(value) => handleProviderChange(value as ModelProvider)}
                          disabled={isStreaming || modelsLoading}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={modelsLoading ? 'Loading...' : 'Provider'} />
                          </SelectTrigger>
                          <SelectContent>
                            {providers.map((provider) => (
                              <SelectItem key={provider.key} value={provider.key}>
                                {provider.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Model selector - mobile only */}
                    {isMobile && !modelsError && (
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Model</Label>
                        <Select
                          value={selectedModelId || ''}
                          onValueChange={handleModelChange}
                          disabled={isStreaming || modelsLoading}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={modelsLoading ? 'Loading...' : 'Model'} />
                          </SelectTrigger>
                          <SelectContent>
                            {(modelsByProvider[selectedProvider] || []).map((model) => (
                              <SelectItem key={model.entityId} value={model.entityId}>
                                <div className="flex items-center gap-1.5">
                                  <span>{model.name}</span>
                                  {model.isRecommended && (
                                    <span className="text-xs text-yellow-500">★</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Global Parameters */}
                    <div className="flex flex-col gap-4">
                      <Label className="text-sm font-medium">Global Parameters</Label>
                      {/* Context checkbox */}
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="include-context-mobile"
                          checked={includeContext}
                          onCheckedChange={(checked) => {
                            const value = checked === true;
                            setIncludeContext(value);
                            updateSingleAIPreferences({ includeContext: value });
                          }}
                          disabled={isStreaming}
                        />
                        <Label htmlFor="include-context-mobile" className="text-sm cursor-pointer">
                          Include conversation context
                        </Label>
                      </div>
                      {/* Global Show Thinking controls */}
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm text-muted-foreground">Show Thinking</Label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Enable thinking for all stored models
                              setAllModelParameters((prev) => {
                                const updated = { ...prev };
                                for (const modelId of Object.keys(updated)) {
                                  updated[modelId] = {
                                    ...updated[modelId],
                                    showThinking: true,
                                    thinkingEnabled: true,
                                  };
                                }
                                // Also update current model if not stored yet
                                if (selectedModel?.entityId && !updated[selectedModel.entityId]) {
                                  updated[selectedModel.entityId] = {
                                    showThinking: true,
                                    thinkingEnabled: true,
                                  };
                                }
                                return updated;
                              });
                              setShowThinking(true);
                              updateSingleAIPreferences({ showThinking: true });
                              toast({ description: 'Show thinking enabled for all models' });
                            }}
                            disabled={isStreaming}
                          >
                            All On
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Disable thinking for all stored models
                              setAllModelParameters((prev) => {
                                const updated = { ...prev };
                                for (const modelId of Object.keys(updated)) {
                                  updated[modelId] = {
                                    ...updated[modelId],
                                    showThinking: false,
                                    thinkingEnabled: false,
                                  };
                                }
                                // Also update current model if not stored yet
                                if (selectedModel?.entityId && !updated[selectedModel.entityId]) {
                                  updated[selectedModel.entityId] = {
                                    showThinking: false,
                                    thinkingEnabled: false,
                                  };
                                }
                                return updated;
                              });
                              setShowThinking(false);
                              updateSingleAIPreferences({ showThinking: false });
                              toast({ description: 'Show thinking disabled for all models' });
                            }}
                            disabled={isStreaming}
                          >
                            All Off
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Divider between global and model-specific settings */}
                    <div className="border-t-4 border-white my-2" />

                    {/* Model parameters - rendered from Entity schema */}
                    {parameterSchema.length > 0 && !modelDefLoading && (
                      <EntityParameterPanel
                        parameterSchema={parameterSchema}
                        values={modelParameters}
                        onChange={handleParameterChange}
                        disabled={isStreaming}
                        title={`${selectedModel?.name || 'Model'} Parameters`}
                        showReset={true}
                        priorityParams={
                          isGpt5Model ? ['reasoningEnabled', 'showThinking'] : undefined
                        }
                      />
                    )}

                    {/* Fallback message when no schema is available */}
                    {parameterSchema.length === 0 && !modelDefLoading && selectedModel && (
                      <div className="border-t border-border pt-4 text-sm text-muted-foreground">
                        <p>Parameter configuration not available for this model.</p>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              {/* Provider selector - desktop only (mobile uses sheet) */}
              {!isMobile && modelsError && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refreshModels()}
                  className="text-destructive"
                >
                  Retry ({modelsError.message})
                </Button>
              )}
              {!isMobile && !modelsError && (
                <Select
                  value={selectedProvider}
                  onValueChange={(value) => handleProviderChange(value as ModelProvider)}
                  disabled={isStreaming || modelsLoading}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder={modelsLoading ? 'Loading...' : 'Provider'} />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((provider) => (
                      <SelectItem key={provider.key} value={provider.key}>
                        {provider.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Model selector - desktop only (mobile uses sheet) */}
              {!isMobile && !modelsError && (
                <Select
                  value={selectedModelId || ''}
                  onValueChange={handleModelChange}
                  disabled={isStreaming || modelsLoading}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={modelsLoading ? 'Loading...' : 'Model'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(modelsByProvider[selectedProvider] || []).map((model) => (
                      <SelectItem key={model.entityId} value={model.entityId}>
                        <div className="flex items-center gap-1.5">
                          <span>{model.name}</span>
                          {model.isRecommended && (
                            <span className="text-xs text-yellow-500">★</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div ref={containerRef} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {/* Don't render anything while restoring session/conversation to avoid flash */}
            {!sessionInitialized || (conversation?.messages?.length && messages.length === 0) ? (
              <div className="h-full min-h-[400px]" />
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <img
                  src="/fias-logo-image.png"
                  alt="Fias AI"
                  className="h-16 w-16 mb-4 dark:invert dark:brightness-90"
                />
                <h2 className="text-xl font-medium text-foreground mb-2">
                  Start a Conversation with {getProviderLabel(selectedProvider)}
                </h2>
                <p className="text-muted-foreground max-w-md">
                  Select your preferred AI provider above and type your message to start a
                  conversation.
                </p>
              </div>
            ) : (
              <div ref={messagesContainerRef} className="space-y-6">
                {/* Learn More popup for text selection */}
                {highlight.isVisible && highlight.position && (
                  <LearnMorePopup
                    position={highlight.position}
                    onClick={highlight.handleLearnMore}
                  />
                )}
                {messages.map((message) => (
                  <React.Fragment key={message.id}>
                    <div
                      className={cn(
                        'flex gap-4',
                        message.role === 'user' ? 'justify-end' : 'justify-start',
                      )}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={cn(
                          'max-w-[80%] rounded-2xl px-4 py-3',
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted',
                        )}
                      >
                        {message.role === 'assistant' && message.provider && (
                          <div className="text-xs text-muted-foreground mb-1">
                            {message.model || getProviderLabel(message.provider)}
                          </div>
                        )}
                        {/* Thinking section - shown if message has thinking content */}
                        {message.thinking && (
                          <div className="mb-3 pb-3 border-b border-border/50">
                            <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                              <span className="opacity-70">Thinking...</span>
                            </div>
                            <div className="text-sm italic text-muted-foreground whitespace-pre-wrap break-words">
                              {message.thinking}
                              {message.isStreaming && !message.content && (
                                <span className="inline-block w-2 h-3 bg-current ml-0.5 animate-pulse" />
                              )}
                            </div>
                          </div>
                        )}
                        {/* Main response content */}
                        <div
                          className={cn(
                            'break-words',
                            message.role === 'assistant' &&
                              'prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-strong:text-foreground',
                          )}
                        >
                          {message.isStreaming && !message.content && !message.thinking ? (
                            <div className="flex gap-0.5">
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '0ms' }}
                              />
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '150ms' }}
                              />
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '300ms' }}
                              />
                            </div>
                          ) : message.role === 'assistant' ? (
                            <>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.content}
                              </ReactMarkdown>
                              {message.isStreaming && message.content && (
                                <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                              )}
                            </>
                          ) : (
                            <>
                              {message.content}
                              {message.isStreaming && message.content && (
                                <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {message.role === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                          <User className="h-4 w-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
                <div ref={anchorRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 border-t border-border bg-background">
          <div className="max-w-3xl mx-auto px-4 py-4">
            {/* Attached files display */}
            {attachedFiles.length > 0 && (
              <AttachedFiles
                files={attachedFiles}
                totalSize={attachedFilesSize}
                onRemove={removeAttachedFile}
                onClearAll={clearAttachedFiles}
                className="mb-3"
              />
            )}
            <form onSubmit={handleSubmit} className="relative">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => setFilePickerOpen(true)}
                disabled={isStreaming || isProcessingFiles}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                title="Attach file from storage"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  attachedFiles.length > 0
                    ? 'Ask about the attached files...'
                    : 'Type your message or drop files for context...'
                }
                disabled={isStreaming || isProcessingFiles}
                className="min-h-[44px] max-h-[200px] pl-12 pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                rows={1}
                style={{ height: 'auto' }}
              />
              {isStreaming ? (
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  onClick={handleStop}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                >
                  <Square className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  disabled={!inputValue.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </form>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>

        {/* Share Dialog */}
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          shareUrl={shareUrl}
          expiresAt={shareExpiresAt}
          loading={shareLoading}
          error={shareError}
        />

        {/* File Picker Dialog */}
        <FilePickerDialog
          open={filePickerOpen}
          onOpenChange={setFilePickerOpen}
          onFileSelected={addFileWithContent}
          isProcessing={isProcessingFiles}
        />
      </div>
    </FileContextDropZone>
  );
}

// Outer component that wraps with ArcheLayout (which provides SearchResultsProvider)
export function SingleAIPage() {
  return (
    <ArcheLayout>
      <SingleAIContent />
    </ArcheLayout>
  );
}

export default SingleAIPage;
